<script>
import moment from 'moment/moment';
import AuditLogsTable from './AuditLogsTable.vue';

export default {
  name: 'AuditTable',
  components: {
    AuditLogsTable,
  },
  props: {
    isLoading: {
      type: Boolean,
      required: true,
      default: false,
    },
    items: {
      type: Array,
      required: true,
    },
    pagination: {
      type: Object,
      default: () => ({
        page: 1,
        pageSize: 2,
        total: 0,
      }),
    },
  },
  data: () => ({
    fields: [
      { key: 'transaction_id', label: 'Transaction ID' },
      { key: 'application_name', label: 'Application Name' },
      { key: 'action_type', label: 'Action Type' },
      { key: 'action_description', label: 'Action Description' },
      { key: 'actor_type', label: 'Actor Type' },
      { key: 'actor_email', label: 'Actor Email' },
      { key: 'target_type', label: 'Target Type' },
      { key: 'target_id', label: 'Target ID' },
      { key: 'target_reference', label: 'Target Reference' },
      { key: 'entity_type', label: 'Entity Type' },
      { key: 'entity_id', label: 'Entity ID' },
      { key: 'client_id', label: 'Client ID' },
      { key: 'origin', label: 'Origin' },
      { key: 'tags', label: 'Tags' },
      { key: 'completed_at', label: 'Completed At' },
      { key: 'is_bulk', label: 'Is Bulk' },
      { key: 'more', label: 'More' },
    ],
  }),
  mounted() {
    this.emitPageChange(this.pagination.page);
  },
  methods: {
    getActionTypeLabel(actionType) {
      switch (actionType) {
        case 'creation':
          return 'Create';
        case 'deletion':
          return 'Delete';
        case 'modification':
          return 'Update';
        case 'retrieval':
          return 'View';
        default:
          return actionType;
      }
    },
    getApplicationNameLabel(applicationName) {
      switch (applicationName) {
        case 'api-administration':
          return 'UI Admin';
        case 'api-therapist':
          return 'Clinical Portal';
        default:
          return applicationName;
      }
    },
    emitPageChange(newPage) {
      this.$emit('page-changed', newPage);
    },
    normalizeDate(date) {
      if (!date) {
        return 'No date';
      }

      return moment(date).format('YYYY-MM-DD HH:mm');
    },
    formatID(id) {
      if (!id) {
        return 'No ID';
      }

      return id.length > 8 ? `${id.slice(0, 8)}...` : id;
    },
    formatClientId(clientId) {
      if (!clientId) {
        return 'No client ID';
      }
      return clientId;
    },
    formatEmail(email) {
      if (!email) {
        return 'No email';
      }

      return email;
    },
    formatReference(reference) {
      if (!reference) {
        return 'No reference';
      }

      return reference;
    },
    lessTenMinutes(date) {
      return moment(date).subtract(10, 'minutes').toISOString();
    },
    addTenMinutes(date) {
      return moment(date).add(10, 'minutes').toISOString();
    },
    getAuditDetails(item) {
      this.$emit('show-log-details', item);
    },
    loadTransactionLogs(rowData) {
      const params = {
        page: rowData.item.transactionLogs.pagination.page,
        page_size: rowData.item.transactionLogs.pagination.pageSize,
        created_at_from: this.lessTenMinutes(rowData.item.created_at),
        created_at_to: this.addTenMinutes(rowData.item.completed_at),
      };
      this.$emit('load-transaction-logs', rowData.item.transaction_id, params);
      rowData.toggleDetails();
    },
    handlePaginationChange(transactionId, newPagination, createdAt) {
      const params = {
        page: newPagination.page,
        page_size: newPagination.pageSize,
        created_at_from: this.lessTenMinutes(createdAt),
        created_at_to: this.addTenMinutes(createdAt),
      };
      this.$emit('load-transaction-logs', transactionId, params);
    },
  },
};
</script>

<template>
  <div>
    <b-row>
      <b-col>
        <b-table
          id="audit-table"
          :fields="fields"
          :items="items"
          :busy="isLoading"
          sticky-header="850px"
          bordered
          hover
          striped
          show-empty
        >
          <template #table-busy>
            <div class="text-center text-danger my-2">
              <b-spinner class="align-middle" />
              <strong>Loading...</strong>
            </div>
          </template>
          <template #empty>
            <div class="text-center my-2">
              <strong>No audit transactions to show</strong>
            </div>
          </template>
          <template #cell(action_type)="data">
            <span v-b-tooltip.hover.top="data.item.action_type">
              {{ getActionTypeLabel(data.item.action_type) }}
            </span>
          </template>
          <template #cell(application_name)="data">
            <span v-b-tooltip.hover.top="data.item.application_name">
              {{ getApplicationNameLabel(data.item.application_name) }}
            </span>
          </template>
          <template #cell(uuid)="data">
            <span v-b-tooltip.hover.top="data.item.uuid">
              {{ formatID(data.item.uuid) }}
            </span>
          </template>
          <template #cell(transaction_id)="data">
            <span v-b-tooltip.hover.top="data.item.transaction_id">
              {{ formatID(data.item.transaction_id) }}
            </span>
          </template>
          <template #cell(actor_email)="data">
            <span v-b-tooltip.hover.top="data.item.actor_id">
              {{ formatEmail(data.item.actor_email) }}
            </span>
          </template>
          <template #cell(target_id)="data">
            <span v-b-tooltip.hover.top="data.item.target_id">
              {{ formatID(data.item.target_id) }}
            </span>
          </template>
          <template #cell(target_reference)="data">
            <span v-b-tooltip.hover.top="data.item.target_id">
              {{ formatReference(data.item.target_reference) }}
            </span>
          </template>
          <template #cell(entity_id)="data">
            <span v-b-tooltip.hover.top="data.item.entity_id">
              {{ formatID(data.item.entity_id) }}
            </span>
          </template>
          <template #cell(client_id)="data">
            <span>
              {{ formatClientId(data.item.client_id) }}
            </span>
          </template>
          <template #cell(origin)="data">
            <span v-b-tooltip.hover.top="data.item.origin">
              {{ data.item.origin || 'N/A' }}
            </span>
          </template>
          <template #cell(tags)="data">
            <b-badge
              v-for="(tag, i) in data.item.tags"
              :key="tag + i"
              variant="primary"
              class="mr-1"
            >
              {{ tag }}
            </b-badge>
          </template>
          <template #cell(created_at)="data">
            <span v-b-tooltip.hover.top="data.item.created_at">
              {{ normalizeDate(data.item.created_at) }}
            </span>
          </template>
          <template #cell(completed_at)="data">
            <span v-b-tooltip.hover.top="data.item.completed_at">
              {{ normalizeDate(data.item.completed_at) }}
            </span>
          </template>
          <template #cell(is_bulk)="data">
            <span v-b-tooltip.hover.top="data.item.is_bulk">
              <b-badge
                v-if="data.item.is_bulk"
                variant="success"
              >
                Yes
              </b-badge>
              <b-badge
                v-else
                variant="danger"
              >
                No
              </b-badge>
            </span>
          </template>

          <template #row-details="data">
            <div class="ml-5">
              <AuditLogsTable
                :is-loading="data.item.transactionLogs.isLoading"
                :items="data.item.transactionLogs.items"
                :pagination="data.item.transactionLogs.pagination"
                :transaction-id="data.item.transaction_id"
                @pagination-changed="handlePaginationChange"
                @show-sublog-details="rowData => $emit('show-sublog-details', rowData)"
              />
            </div>
          </template>

          <template #cell(more)="data">
            <div class="d-flex justify-content-center">
              <b-button
                variant="primary"
                size="sm"
                @click="getAuditDetails(data.item)"
              >
                <feather
                  type="eye"
                  stroke="white"
                />
              </b-button>

              <b-button
                v-if="data.item.is_bulk"
                class="ml-2"
                variant="primary"
                size="sm"
                @click="loadTransactionLogs(data)"
              >
                <feather
                  type="list"
                  stroke="white"
                />
              </b-button>
            </div>
          </template>
        </b-table>
      </b-col>
    </b-row>

    <b-row>
      <b-col>
        <b-pagination
          align="center"
          :value="pagination.page"
          :total-rows="pagination.total"
          :per-page="pagination.pageSize"
          @change="emitPageChange"
        />
      </b-col>
    </b-row>
  </div>
</template>

<style>
.nested-table-container {
  max-height: 500px;
  overflow-y: auto;
}
</style>
